import { VIPMember, MembershipType } from '../models/VIPMember';

/**
 * VIP 회원 관리 서비스
 */
export class VIPMemberService {
  /**
   * 멤버십 타입별 스타일 클래스 반환
   */
  getMembershipStyle(type: MembershipType): string {
    switch (type) {
      case 'diamond':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'platinum':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'gold':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  }

  /**
   * 멤버십 타입별 배경색 클래스 반환
   */
  getMembershipCardStyle(type: MembershipType): string {
    switch (type) {
      case 'diamond':
        return 'bg-purple-50';
      case 'platinum':
        return 'bg-gray-50';
      case 'gold':
        return 'bg-yellow-50';
      default:
        return 'bg-white';
    }
  }

  /**
   * 멤버십 타입별 텍스트 색상 클래스 반환
   */
  getMembershipTextColor(type: MembershipType): string {
    switch (type) {
      case 'diamond':
        return 'text-purple-600';
      case 'platinum':
        return 'text-gray-600';
      case 'gold':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  }

  /**
   * 멤버십 타입 한글 이름 반환
   */
  getMembershipLabel(type: MembershipType): string {
    switch (type) {
      case 'diamond':
        return '다이아몬드';
      case 'platinum':
        return '플래티넘';
      case 'gold':
        return '골드';
      default:
        return '';
    }
  }

  /**
   * 회원 검색 (이름, 전화번호, 이메일)
   */
  filterMembers(
    members: VIPMember[],
    searchTerm: string,
    membershipType: string
  ): VIPMember[] {
    return members.filter((member) => {
      const matchesSearch =
        member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.phone.includes(searchTerm) ||
        member.email?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType =
        membershipType === 'all' || member.membership_type === membershipType;

      return matchesSearch && matchesType;
    });
  }

  /**
   * 날짜 포맷팅 (YYYY-MM-DD → YYYY년 MM월 DD일)
   */
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('ko-KR');
  }

  /**
   * 전화번호 포맷팅 (010-1234-5678)
   */
  formatPhone(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return phone;
  }

  /**
   * 지정석 유무 확인
   */
  hasSeatAssignment(member: VIPMember): boolean {
    return member.assigned_seat !== undefined && member.assigned_seat !== null;
  }

  /**
   * 활성 회원 필터링
   */
  filterActiveMembers(members: VIPMember[]): VIPMember[] {
    return members.filter((member) => member.is_active);
  }

  /**
   * 멤버십 타입별 회원 수 계산
   */
  countByMembershipType(
    members: VIPMember[],
    type: MembershipType
  ): number {
    return members.filter((member) => member.membership_type === type).length;
  }
}

export const vipMemberService = new VIPMemberService();
